import { useTranslation } from 'react-i18next';
import {
  useGetBConnectInterventionTypes,
  useGetExocetClientCities,
  useGetExocetClientContracts,
  useGetExocetClientStreets,
  useGetExocetDiameters,
  useGetExocetFamilies
} from '../../../../hooks/datarefs';
import { translateType } from '../NewIntervention/Steps/utils';
import { useInterventionCreation } from './context/interventionCreationContext';
import { FilterField, FilterOption } from '../../../Tools/Table/TableFilters';
import { TableFilters } from '../../../Tools/Table/filters';

/**
 * Filtre « État », présenté dans la barre d'outils des deux étapes plutôt que dans le
 * bloc dépliable.
 *
 * Seul le libellé de l'option « en attente » distingue les étapes — « à qualifier » ou
 * « à affecter » — d'où `pendingLabelKey` : le paramètre envoyé, lui, reste le même.
 */
export function useStateFilterField(pendingLabelKey: string): FilterField {
  const { t } = useTranslation();

  return {
    name: 'in_progress',
    labelKey: 'interventionCreation.filters.state',
    // L'option « Tous » portant la valeur vide, c'est la branche « rien de choisi » qui
    // s'affiche : on la fait pointer sur le même libellé, une seule source de vérité.
    allLabelKey: 'interventionCreation.filters.allRequests',
    options: [
      // Valeur vide : « Tous » ne restreint rien, donc le paramètre ne partira pas.
      // C'est aussi l'option retenue au chargement, faute de filtre posé.
      { value: '', label: t('interventionCreation.filters.allRequests') },
      { value: 'true', label: t(pendingLabelKey) }
    ]
  };
}

/**
 * Les référentiels de communes et de rues sont servis par des routes différentes selon
 * l'étape : chacune fournit les siennes.
 */
export type FilterEndpoints = {
  citiesUrl: string;
  streetsUrl: string;
};

export const QUALIFY_FILTER_ENDPOINTS: FilterEndpoints = {
  citiesUrl: '/api/boi/exocet/qualify-customer-cities',
  streetsUrl: '/api/boi/exocet/pds/qualify-streets/'
};

export const ASSIGN_FILTER_ENDPOINTS: FilterEndpoints = {
  citiesUrl: '/api/boi/exocet/assign-technician-customer-cities',
  streetsUrl: '/api/boi/exocet/assign-technician-streets/'
};

/** Les familles ont un libellé métier propre, repris de la v1. */
const FAMILY_LABEL_KEYS: Record<string, string> = {
  WaterMeter: 'intervention.waterMeter',
  Repetition: 'intervention.repetitionEquipment',
  FirePost: 'intervention.firePost',
  WaterHydrant: 'intervention.waterHydrant',
  LeakDetection: 'intervention.leakDetection',
  PAV: 'intervention.pav',
  LeakPreLocator: 'intervention.leakPreLocator',
  LuKa: 'intervention.luka',
  'API-BI': 'intervention.apiBi'
};

type ReferenceOption = { value: string | number; label: string };

/**
 * Champs de la barre de filtres.
 *
 * `filters` est lu pour la rue : la liste des rues dépend des communes cochées, comme
 * en v1.
 */
export function useFilterFields(
  endpoints: FilterEndpoints,
  filters: TableFilters,
  options: { withInterventionType?: boolean; withDiameter?: boolean } = {}
): FilterField[] {
  const { t } = useTranslation();
  const { state } = useInterventionCreation();
  const client = state.clientId === null ? '' : String(state.clientId);

  const selectedCities = Array.isArray(filters.city_id__in) ? filters.city_id__in : [];

  const { data: contracts } = useGetExocetClientContracts(client);
  const { data: cities } = useGetExocetClientCities(client, endpoints.citiesUrl);
  const { data: streets } = useGetExocetClientStreets(
    client,
    selectedCities.length > 0 ? selectedCities.join(',') : undefined,
    endpoints.streetsUrl
  );
  const { data: families } = useGetExocetFamilies();
  const { data: diameters } = useGetExocetDiameters(client);
  const { data: interventionTypes } = useGetBConnectInterventionTypes();

  const referenceOptions = (values: ReferenceOption[] | undefined): FilterOption[] =>
    (values ?? []).map((option) => ({ value: option.value, label: String(option.label) }));

  const interventionTypeField: FilterField[] = options.withInterventionType
    ? [
        {
          name: 'intervention_type_code',
          allLabelKey: 'interventionCreation.filters.all',
          labelKey: 'interventionCreation.filters.interventionType',
          multiple: true,
          options: (interventionTypes ?? []).map((type) => ({
            value: type.Id,
            label: translateType(type.Name, t)
          }))
        }
      ]
    : [];

  const diameterField: FilterField[] = options.withDiameter
    ? [
        {
          name: 'equipment_diameter__in',
          allLabelKey: 'interventionCreation.filters.all',
          labelKey: 'interventionCreation.filters.diameter',
          multiple: true,
          // Seul filtre à ne pas passer par `referenceOptions` : l'API filtre sur la valeur
          // du diamètre, pas sur son identifiant, et le référentiel expose la valeur en
          // `label` et l'identifiant en `value` — l'inverse des autres. La v1 traitait déjà
          // ce cas à part.
          options: (diameters ?? []).map((diameter) => ({
            value: String(diameter.label),
            label: String(diameter.label)
          }))
        }
      ]
    : [];

  return [
    {
      name: 'contract_id__in',
      allLabelKey: 'interventionCreation.filters.all',
      labelKey: 'interventionCreation.filters.contract',
      multiple: true,
      options: referenceOptions(contracts)
    },
    {
      name: 'city_id__in',
      allLabelKey: 'interventionCreation.filters.allFeminine',
      labelKey: 'interventionCreation.filters.city',
      multiple: true,
      options: referenceOptions(cities)
    },
    {
      name: 'address_street_name__in',
      allLabelKey: 'interventionCreation.filters.allFeminine',
      labelKey: 'interventionCreation.filters.street',
      multiple: true,
      options: referenceOptions(streets)
    },
    {
      name: 'sensor_family_type__in',
      allLabelKey: 'interventionCreation.filters.allFeminine',
      labelKey: 'interventionCreation.filters.family',
      multiple: true,
      options: (families ?? []).map((family) => ({
        value: family.Name,
        label: FAMILY_LABEL_KEYS[family.Name] ? t(FAMILY_LABEL_KEYS[family.Name]) : family.Name
      }))
    },
    ...interventionTypeField,
    ...diameterField
  ];
}
